import type { GetVitalSignsResponse, VentilationWithTypeKey, VitalSignItemDTO } from '@/generated'
import { VentilationApi, VitalSignsApi } from '@/generated'
import { globalApiConfig } from '@/services/telehealthApi/APIConfig'

export class VitalSignsService {
  static readonly vitalSignsApi = new VitalSignsApi(globalApiConfig)
  static readonly ventilationApi = new VentilationApi(globalApiConfig)

  static async getVitalSigns(
    caseId: string,
    startTime: string,
    endTime: string,
    samplingMinutes?: number,
    shareCode?: string
  ): Promise<{
    temperature: VitalSignItemDTO[]
    sp02: VitalSignItemDTO[]
    respiratoryRate: VitalSignItemDTO[]
  }> {
    const response = await VitalSignsService.vitalSignsApi.getVitalSigns(
      caseId,
      ['TEMP', 'SPO2', 'RR'],
      startTime,
      endTime,
      samplingMinutes,
      shareCode
    )

    return {
      temperature: response.data.items.flatMap((item) =>
        item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'TEMP')
      ),
      sp02: response.data.items.flatMap((item) =>
        item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'SPO2')
      ),
      respiratoryRate: response.data.items.flatMap((item) =>
        item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'RR')
      )
    }
  }

  static async getLatestVitalSigns(
    caseId: string,
    shareCode: string | undefined
  ): Promise<GetVitalSignsResponse> {
    return (
      await VitalSignsService.vitalSignsApi.getLatestVitalSigns(
        caseId,
        [
          'TEMP',
          'SPO2',
          'RR',
          'HEART_RATE',
          'INV_D_BP',
          'INV_M_BP',
          'INV_S_BP',
          'NON_INV_D_BP',
          'NON_INV_M_BP',
          'NON_INV_S_BP'
        ],
        shareCode
      )
    ).data
  }

  static async getHeartRateDataByCaseId(
    caseId: string,
    startTime: string,
    endTime: string,
    samplingMinutes?: number,
    shareCode?: string
  ): Promise<{
    heartRate: VitalSignItemDTO[]
    invasiveBloodPressureMax: VitalSignItemDTO[]
    invasiveBloodPressureMin: VitalSignItemDTO[]
    invasiveBloodPressureAverage: VitalSignItemDTO[]
    nonInvasiveBloodPressureMax: VitalSignItemDTO[]
    nonInvasiveBloodPressureMin: VitalSignItemDTO[]
    nonInvasiveBloodPressureAverage: VitalSignItemDTO[]
  }> {
    {
      const response = await VitalSignsService.vitalSignsApi.getVitalSigns(
        caseId,
        [
          'HEART_RATE',
          'INV_D_BP',
          'INV_S_BP',
          'INV_M_BP',
          'NON_INV_D_BP',
          'NON_INV_S_BP',
          'NON_INV_M_BP'
        ],
        startTime,
        endTime,
        samplingMinutes,
        shareCode
      )

      return {
        heartRate: response.data.items.flatMap((item) =>
          item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'HEART_RATE')
        ),
        invasiveBloodPressureMax: response.data.items.flatMap((item) =>
          item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'INV_S_BP')
        ),
        invasiveBloodPressureMin: response.data.items.flatMap((item) =>
          item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'INV_D_BP')
        ),
        invasiveBloodPressureAverage: response.data.items.flatMap((item) =>
          item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'INV_M_BP')
        ),
        nonInvasiveBloodPressureMax: response.data.items.flatMap((item) =>
          item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'NON_INV_S_BP')
        ),
        nonInvasiveBloodPressureMin: response.data.items.flatMap((item) =>
          item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'NON_INV_D_BP')
        ),
        nonInvasiveBloodPressureAverage: response.data.items.flatMap((item) =>
          item.vitalSigns.filter((vitalSign) => vitalSign.typeKey === 'NON_INV_M_BP')
        )
      }
    }
  }

  static async getLatestVentilationDataByCaseId(
    caseId: string,
    shareCode?: string
  ): Promise<VentilationWithTypeKey[]> {
    const { data } = await VitalSignsService.ventilationApi.getLatestVentilationData(
      caseId,
      shareCode
    )

    return data.items
  }

  static async getLatestVitalSignsByCaseId(
    caseId: string,
    shareCode?: string
  ): Promise<{
    heartRate: number | string
    bloodPressure: number | string
    spo2: number | string
  }> {
    const { data } = await VitalSignsService.vitalSignsApi.getLatestVitalSigns(
      caseId,
      [
        'HEART_RATE',
        'INV_D_BP',
        'INV_S_BP',
        'INV_M_BP',
        'NON_INV_D_BP',
        'NON_INV_S_BP',
        'NON_INV_M_BP',
        'SPO2'
      ],
      shareCode
    )

    const { items } = data

    let invasiveBloodPressureS =
      items.find((item) => item.typeKey === 'INV_S_BP')?.vitalSigns[0].numericValue ?? 0
    let invasiveBloodPressureD =
      items.find((item) => item.typeKey === 'INV_D_BP')?.vitalSigns[0].numericValue ?? 0
    let invasiveBloodPressureM =
      items.find((item) => item.typeKey === 'INV_M_BP')?.vitalSigns[0].numericValue ?? 0

    if (
      [invasiveBloodPressureS, invasiveBloodPressureD, invasiveBloodPressureM].some(
        (value) => !value
      )
    ) {
      invasiveBloodPressureS =
        items.find((item) => item.typeKey === 'NON_INV_S_BP')?.vitalSigns[0].numericValue ?? 0
      invasiveBloodPressureD =
        items.find((item) => item.typeKey === 'NON_INV_D_BP')?.vitalSigns[0].numericValue ?? 0
      invasiveBloodPressureM =
        items.find((item) => item.typeKey === 'NON_INV_M_BP')?.vitalSigns[0].numericValue ?? 0
    }

    return {
      heartRate:
        items
          .find((item) => item.typeKey === 'HEART_RATE')
          ?.vitalSigns[0].numericValue.toFixed(0) ?? 0,
      bloodPressure: `${invasiveBloodPressureS.toFixed(0)}/${invasiveBloodPressureD.toFixed(0)} (${invasiveBloodPressureM.toFixed(0)})`,
      spo2:
        items.find((item) => item.typeKey === 'SPO2')?.vitalSigns[0].numericValue.toFixed(0) ?? 0
    }
  }
}
